/**
 * Converts a page name or path (optionally with subfolders) to a URL path segment.
 * Matches ComponentWriter's getRoutePathFromFilePath: lowercase, spaces to dashes.
 * @param {string} pageNamePart - Page name or path like "Executives" or "Settings/Profile"
 * @returns {string} URL path e.g. "/executives" or "/settings/profile"
 */
function pageNameToPath(pageNamePart) {
    const segments = pageNamePart
        .split("/")
        .map((s) =>
            s
                .replace(/\.(jsx|tsx|js|ts)$/i, "")
                .replace(/\s+/g, "-")
                .toLowerCase()
        )
        .filter(Boolean);
    return segments.length ? `/${segments.join("/")}` : ".";
}

/**
 * Creates a URL path for a given page component name
 * @param {string} pageName - The name of the page component, optionally with query parameters.
 *   For pages in subfolders, use path form: "Settings/Profile" or "Admin/Dashboard" so the URL matches the route (e.g. /settings/profile).
 * @param {Object} [params] - Optional query parameters as key-value pairs to add/merge
 * @returns {string} The URL path for the page
 *
 * @example
 * createPageUrl("Executives") // returns "/executives"
 * createPageUrl("Settings/Profile") // returns "/settings/profile" (subfolder)
 * createPageUrl("Executives?id=123&view=grid") // returns "/executives?id=123&view=grid"
 * createPageUrl("UserProfile?userId=456") // returns "/userprofile?userId=456"
 * createPageUrl("Executives", { id: 123, view: "grid" }) // returns "/executives?id=123&view=grid"
 * createPageUrl("Executives?view=grid", { id: 123 }) // returns "/executives?view=grid&id=123"
 */
export function createPageUrl(pageName, params = {}) {
    if (!pageName) {
        return ".";
    }

    // Check if there are query parameters in the pageName
    const questionMarkIndex = pageName.indexOf("?");

    // Extract base page name and existing query string
    const pageNamePart =
        questionMarkIndex === -1
            ? pageName
            : pageName.substring(0, questionMarkIndex);
    const existingQueryString =
        questionMarkIndex === -1
            ? ""
            : pageName.substring(questionMarkIndex + 1);

    // Create URLSearchParams and add existing params
    const searchParams = new URLSearchParams(existingQueryString);

    // Add/merge params from the params object
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, value);
        }
    });

    const basePath = pageNameToPath(pageNamePart);
    if (basePath === ".") {
        return ".";
    }

    // If there are no valid params, return just the path
    if (searchParams.toString() === "") {
        return basePath;
    }

    return `${basePath}?${searchParams.toString()}`;
}

/**
 * Creates a URL path with query parameters from an object
 * @param {string} pageName - The name of the page component (use "Folder/Page" for subfolder pages)
 * @param {Object} params - Query parameters as key-value pairs
 * @returns {string} The URL path with query string
 *
 * @example
 * createPageUrlWithParams("Executives", { id: 123, view: "grid" })
 * // returns "/executives?id=123&view=grid"
 * createPageUrlWithParams("Settings/Profile", { userId: 456 })
 * // returns "/settings/profile?userId=456"
 */
/**
 * Creates a full URL to a page in another app (cross-app deep link).
 * Automatically appends the current user's auth token for seamless SSO.
 * @param {string} appDomain - The domain of the target app (e.g. "happy-blue-mountain.agentui.app")
 * @param {string} pageName - The page name in the target app (e.g. "Dashboard" or "Settings/Profile")
 * @param {Object} [params] - Optional query parameters
 * @returns {string} Full URL with auth token
 *
 * @example
 * createAppLink("my-app.agentui.app", "Dashboard")
 * // => "https://my-app.agentui.app/dashboard?x_token_auth=..."
 * createAppLink("my-app.agentui.app", "Settings/Profile", { userId: "123" })
 * // => "https://my-app.agentui.app/settings/profile?userId=123&x_token_auth=..."
 */
export function createAppLink(appDomain, pageName, params = {}) {
    const basePath = pageNameToPath(pageName);
    const protocol = appDomain.includes("localhost") ? "http" : "https";

    // Portal flow: system token exists, build direct URL
    const systemToken = window.__systemToken || null;
    if (systemToken) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null)
                searchParams.append(key, value);
        });
        searchParams.append("x_token_auth", systemToken);
        const qs = searchParams.toString();
        return `${protocol}://${appDomain}${basePath}${qs ? "?" + qs : ""}`;
    }

    // Direct-login flow: redirect through backend for cross-app auth
    const workflowId = window.config?.workflowId;
    const localToken = workflowId
        ? localStorage.getItem(`workflow-auth-${workflowId}`)
        : null;

    if (localToken && workflowId) {
        const hostname = window?.location?.hostname;
        const apiUrl =
            hostname === "localhost" || hostname?.includes("agentui.test")
                ? "http://localhost:3333"
                : hostname === "staging.agentui.ai"
                ? "https://staging.api.agentui.ai"
                : "https://api.agentui.ai";

        const searchParams = new URLSearchParams();
        searchParams.append("token", localToken);
        searchParams.append("target", appDomain);
        if (pageName) searchParams.append("page", pageName);
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null)
                searchParams.append(key, value);
        });
        return `${apiUrl}/workflows/${workflowId}/auth/deep-link?${searchParams.toString()}`;
    }

    // No auth available: build plain URL without tokens
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null)
            searchParams.append(key, value);
    });
    const qs = searchParams.toString();
    return `${protocol}://${appDomain}${basePath}${qs ? "?" + qs : ""}`;
}

export function createPageUrlWithParams(pageName, params = {}) {
    // Remove any existing query params from pageName
    const questionMarkIndex = pageName.indexOf("?");
    const basePageName =
        questionMarkIndex === -1
            ? pageName
            : pageName.substring(0, questionMarkIndex);

    const basePath = pageNameToPath(basePageName);
    if (basePath === ".") {
        return ".";
    }

    // Create URLSearchParams from params object
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, value);
        }
    });

    const queryString = searchParams.toString();
    return queryString ? `${basePath}?${queryString}` : basePath;
}
